// The sign-in screen.
//
// Google's own button is used rather than a hand-rolled one: it is the only
// thing that can produce a credential Google will vouch for, and it keeps the
// password out of this application entirely — there is none to store or lose.
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';

const GSI = 'https://accounts.google.com/gsi/client';

function loadGoogle() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.accounts) return resolve(window.google);
    const existing = document.querySelector(`script[src="${GSI}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('could not load Google sign-in')));
      return;
    }
    const s = document.createElement('script');
    s.src = GSI; s.async = true; s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('could not load Google sign-in'));
    document.head.appendChild(s);
  });
}

export default function Login() {
  const { config, signIn } = useAuth();
  const holder = useRef(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    if (!config || !config.clientId) return;
    loadGoogle().then(google => {
      if (dead || !holder.current) return;
      google.accounts.id.initialize({
        client_id: config.clientId,
        callback: async (resp) => {
          setBusy(true); setError(null);
          try { await signIn(resp.credential); }
          catch (e) {
            setError(e.response?.data?.message || e.message);
            setBusy(false);
          }
        }
      });
      google.accounts.id.renderButton(holder.current, {
        theme: 'filled_blue', size: 'large', text: 'signin_with', width: 280
      });
    }).catch(e => setError(e.message));
    return () => { dead = true; };
  }, [config, signIn]);

  return (
    <div style={{
      minHeight: '100vh', background: '#f8f3eb', color: '#2e1f23',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div style={{
        background: '#ffffff', border: '1px solid #e6dccc', borderRadius: 14,
        padding: '38px 34px', width: '100%', maxWidth: 420, textAlign: 'center'
      }}>
        {/* The full lockup, once, where there is room for it to be read. */}
        <img src="/brand/logo-full.png" alt="The Think'th Goods Company"
          style={{ width: 190, height: 'auto', display: 'block', margin: '0 auto 22px' }} />
        <h1 style={{ margin: '0 0 6px', fontSize: 22, fontFamily: "'Erbaum', sans-serif", fontWeight: 700 }}>
          Staff dashboard
        </h1>
        <p style={{ color: '#7d6a63', fontSize: 14, margin: '0 0 26px' }}>
          Sign in with the Google account you were given access with.
        </p>

        <div ref={holder} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />

        {busy && <div style={{ color: '#7d6a63', fontSize: 13, marginTop: 16 }}>Signing you in…</div>}

        {error && (
          <div style={{
            marginTop: 20, background: '#fbe9e7', border: '1px solid #f3cfcb',
            color: '#8c1d18', borderRadius: 8, padding: '10px 12px', fontSize: 13, textAlign: 'left'
          }}>
            {error}
          </div>
        )}

        {config && config.ownerConfigured === false && (
          <div style={{
            marginTop: 20, background: '#f5f1d6', border: '1px solid #7a6a12',
            color: '#7a6a12', borderRadius: 8, padding: '10px 12px', fontSize: 13, textAlign: 'left'
          }}>
            No owner has been nominated yet, so every sign-in will be refused.
            Add <code>OWNER_EMAIL</code> in Railway → Variables.
          </div>
        )}

        <p style={{ color: '#b0a296', fontSize: 12, marginTop: 26, marginBottom: 0 }}>
          Access is by invitation. A Google account on its own is not enough.
        </p>
      </div>
    </div>
  );
}
